import { createStore } from 'vuex'
import axios from 'axios'

// Favorites load / save bookkeeping (not UI state, so kept outside the store's reactive state)
let storeDataLoadID = 0; // bumped on every setStoreData, so late responses from an earlier load are dropped
let favoritesLoad: Promise<boolean> = Promise.resolve(false); // resolves true once saved favorites are loaded
let favoritesSave: Promise<any> = Promise.resolve(); // favorite saves run one after another

export default createStore({
  state: {
    aiDocked: false, // "Improve with AI" open as the side panel in an editor (AIImprovePanel.vue); App.vue folds the menu
    apiUrl: process.env.VUE_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001'),
    spacesUrl: '/api/appfile/',
    spacesUrlPublic: 'https://superform-portal.nyc3.digitaloceanspaces.com/',
    user: {} as any,
    signedIn: false,
    hideChatWidget: false, // hides the external Search Processes widget (App.vue) while a panel it would cover is open
    routeLoaded: false,
    lastActivityAt: Date.now(), // last click/typing/scroll in this tab (activity.ts)
    playbookNodeTypeMap: {
      'NODE': 'Folder',
      'PROCESS': 'Process',
      'TIMELINE': 'Value Engine',
      'SHEET': 'Role Sheet',
      'LINK': 'Link',
      'VIDEO': 'Video',
      'CHECKLIST': 'Powerlist'
    },
    allNodes: [] as any,
    allNodesFiltered: [] as any,
    allNodesLoaded: false,
    adminRolesCanEdit: [ 'Admin', 'Editor' ],
    users: [] as any,
    chatAdded: false,
    employeeFields: [] as any,
    employeeFieldsLoaded: false,
    employeeTeams: [] as any,
    employeeTeamsLoaded: false,
    // Named colors for board tags, columns and card covers. Mid-tone, so tags' white text reads on them and a column's
    // tint (30% of its color) stays soft. Apply Default Colors (CompanyBoard.vue) picks from these by label. Colors
    // saved before this list changed keep their own hex.
    colorPalette: [
      { label: 'Black', hex: '#000000' },
      { label: 'White', hex: '#FFFFFF' },
      { label: 'Light Gray', hex: '#D4D4D4' }, // also the starting color for a new column
      { label: 'Gray', hex: '#6B7075' },
      { label: 'Red', hex: '#D22630' },
      { label: 'Rose', hex: '#D9577B' },
      { label: 'Orange', hex: '#E8912F' },
      { label: 'Gold', hex: '#C9A227' },
      { label: 'Green', hex: '#3E8E4E' },
      { label: 'Teal', hex: '#1E9E9A' },
      { label: 'Blue', hex: '#2F80ED' },
      { label: 'Navy', hex: '#1F3A68' },
      { label: 'Purple', hex: '#8E5CC7' },
      { label: 'Brown', hex: '#8B5E3C' }
    ],
    monthShortStrings: {
      1: 'Jan',
      2: 'Feb',
      3: 'Mar',
      4: 'Apr',
      5: 'May',
      6: 'Jun',
      7: 'Jul',
      8: 'Aug',
      9: 'Sep',
      10: 'Oct',
      11: 'Nov',
      12: 'Dec'
    },
    monthStrings: {
      1: 'January',
      2: 'February',
      3: 'March',
      4: 'April',
      5: 'May',
      6: 'June',
      7: 'July',
      8: 'August',
      9: 'September',
      10: 'October',
      11: 'November',
      12: 'December'
    },
    dayShortStrings: {
      0: 'Sun',
      1: 'Mon',
      2: 'Tue',
      3: 'Wed',
      4: 'Thu',
      5: 'Fri',
      6: 'Sat'
    },
    showSubMenu: false,
    showChangePassword: false,
    // The header's quick add (App.vue): 'task' | 'agenda' | 'numbers' while its popup is open
    quickAdd: null as string | null,
    // Your scorecard numbers still to enter (myNumbers.ts): the header's Numbers button shows while there are any
    numbersToEnter: 0,
    // The header stopwatch's floating timer (FloatingTimer.vue): whether its panel is open, and what
    // it's doing (idle | running | paused | done) with the time left, for the header button
    timer: { open: false, status: 'idle', label: '' } as { open: boolean, status: string, label: string },
    // Fields to start the next agenda item quick add with, e.g. { Name } (used once, then cleared)
    quickAddPreset: null as any,
    // Confirmation after a quick add: { text, link: { label, to } }
    quickAddToast: null as any,
    nodeCounts: {} as any,
    teamHubChecklists: [],
    adminSelectedMenu: 'The Book',
    navFavorites: [] as string[], // ordered nav item keys (see navItems in App.vue), plus 'board:<slug>' for boards
    companyBoards: [] as any[], // boards this user can see; resolves 'board:<slug>' favorites
  },
  getters: {
  },
  mutations: {
  },
  actions: {
    // Adds or removes a favorite key and saves the whole list. Waits for the saved favorites to load
    // first (saving before then would overwrite them with just this key), and saves one at a time so
    // requests reach the server in click order.
    async toggleFavorite(context, key: string) {
      if(!await favoritesLoad) {
        alert('Your favorites could not be loaded. Refresh the page and try again.');
        return;
      }
      const current = this.state.navFavorites;
      const favorites = current.includes(key) ? current.filter((k: string) => k != key) : [...current, key];
      this.state.navFavorites = favorites;
      const loadID = storeDataLoadID;
      favoritesSave = favoritesSave.then(() => axios.post(`${this.state.apiUrl}/api/navfavorites`, { favorites }))
          .catch(async (e: any) => {
              console.log(e);
              alert('There was an error updating your favorites');
              // The server's copy is the source of truth after a failed save
              await axios.get(`${this.state.apiUrl}/api/navfavorites`)
                  .then(resp => {
                      if(loadID == storeDataLoadID) {
                          this.state.navFavorites = resp.data;
                      }
                  })
                  .catch((err: any) => console.log(err));
          });
      await favoritesSave;
    },
    async setStoreData() {
      // Responses from an earlier load (e.g. before a company switch) are ignored
      const loadID = ++storeDataLoadID;

      // Nav favorites
      favoritesLoad = axios.get(`${this.state.apiUrl}/api/navfavorites`)
          .then(resp => {
              if(loadID == storeDataLoadID) {
                  this.state.navFavorites = resp.data;
              }
              return true;
          })
          .catch(e => {
              console.log(e);
              return false;
          })

      // Boards the user can see, so favorited boards can show in the menu
      axios.get(`${this.state.apiUrl}/api/companyboards`)
          .then(resp => {
              if(loadID == storeDataLoadID) {
                  this.state.companyBoards = resp.data;
              }
          })
          .catch(e => {
              console.log(e);
          })

      // Employee Fields
      axios.get(`${this.state.apiUrl}/api/employees/fields`)
            .then(resp => {
                if(resp.data) {
                    this.state.employeeFields = resp.data;
                    this.state.employeeFieldsLoaded = true;
                }
            })
            .catch(e => {
                console.log(e);
            })

      // Employee Teams -- order here drives the accountability chart's team color coding
      axios.get(`${this.state.apiUrl}/api/employees/teams`)
            .then(resp => {
                if(resp.data) {
                    this.state.employeeTeams = resp.data;
                    this.state.employeeTeamsLoaded = true;
                }
            })
            .catch(e => {
                console.log(e);
            })

      if(this.state.user.isAdmin) {
        axios.get(`${this.state.apiUrl}/api/users`)
          .then(resp => {
              this.state.users = resp.data;
          })
          .catch(e => {
              console.log(e);
          })

        // Node counts
        axios.get(`${this.state.apiUrl}/api/playbook/needsapprovalnodes`)
          .then(resp => {
              this.state.nodeCounts.needsApproval = resp.data.length;
          })
          .catch(e => {
              console.log(e);
          })
        axios.get(`${this.state.apiUrl}/api/playbook/needsreviewnodes`)
          .then(resp => {
              this.state.nodeCounts.needsReview = resp.data.length;
          })
          .catch(e => {
              console.log(e);
          })
      }

      // All nodes menu in App.vue
      await axios.get(`${this.state.apiUrl}/api/playbook/nodes`)
          .then(resp => {
              this.state.allNodes = resp.data;
              console.log('here');
              console.log(this.state.allNodes);
              this.state.allNodesFiltered = [];
              for(const node of this.state.allNodes) {
                if(node.TypesJSON) {
                  try {
                    node.TypesString = JSON.parse(node.TypesJSON).join(', ');
                  } catch(e) {
                    // nothing
                  }
                }
              }
          })
          .catch(e => {
              console.log(e);
          })

      this.state.allNodesLoaded = true;

      // Calls that depend on allNodes
      // Team Hub Employee
      axios.get(`${this.state.apiUrl}/api/teamhubemployee`)
        .then(resp => {
          // This same request is what sets the session's employeeID, so keep the client's copy in step
          // with it (the /session snapshot taken at load can hold a stale one, e.g. after a company switch)
          this.state.user.employeeID = resp.data ? parseInt(resp.data.ID) : null;
          if(resp.data) {
            this.state.user.teamHubEmployee = resp.data;
            
            // checklists
            this.state.user.teamHubChecklists = [];
            try {
              const slugs = JSON.parse(this.state.user.teamHubEmployee.ChecklistsJSON);
              for(const slug of slugs) {
                const item = this.state.allNodes.find((n: any) => {
                  return n.Slug == slug;
                })
                if(item) {
                  this.state.user.teamHubChecklists.push(item);
                }
              }
            } catch(e) {
              //
            }

          }

          this.state.user.teamHubEmployeeLoaded = true;
        })
        .catch(e => {
            console.log(e);
            this.state.user.teamHubEmployeeLoaded = true;
        })
    },
  },
  modules: {
  }
})
